// authored by Alex Cooksey
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Dropdown from 'react-bootstrap/Dropdown';
import IcomoonReact from 'icomoon-react';
import { Grid } from '@giphy/react-components';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { Picker } from 'emoji-mart';
import 'emoji-mart/css/emoji-mart.css';

import Wrapper from './Wrapper';
import iconSet from '../../images/teambuildr-selection.json';
import { ColorGreen, ColorPrimary, ColorWhite } from '../../GlobalStyles';

const giphyApiKey = process.env.GIPHY_API_KEY || 'sXpGFDGZs0Dv1mmNFvYaGUvYwKX0PWIh'; // Demo key fallback
const gf = new GiphyFetch(giphyApiKey);

const ButtonWrapper = styled('div')`
  display: flex;
`;

const DropDownMenuWrapper = styled('div')`
  align-items: center;
  border-radius: 20%;
  color: #333333;
  display: flex;
  justify-content: center;
  &.giphy-dropdown {
    cursor: pointer;
  }
  &.emoji-dropdown {
    cursor: pointer;
  }
  &.notification-dropdown {
    position: absolute;
    right: 15px;
  }
  .dropdown-container {
    border: 0px;
    border-radius: 5px;
    box-shadow: 0 2px 4px 0 rgba(234, 234, 234, 0.5);
    margin-bottom: 10px;
    margin-right: -20px;
    &.giphy {
      max-height: 450px;
      overflow-y: auto;
      padding: 10px;
      z-index: 100;
      img {
        border-radius: 4px;
        cursor: pointer;
      }
    }
    &.emoji {
      padding: 0px;
      z-index: 100;
    }
  }
  .dropdown-divider {
    margin: 0px;
  }
  .dropdown-item {
    align-items: center;
    display: flex;
    padding: 15px 20px;
    span {
      color: ${ColorPrimary};
    }
    .drop-icon-text {
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 0px;
      margin-left: 10px;
      padding-top: 2px;
      text-transform: none;
    }
    :hover {
      background: #006a65;
      color: ${ColorWhite};
      transition: linear 0s;
      span {
        color: ${ColorWhite};
      }
      svg {
        fill: ${ColorWhite};
      }
    }
  }
  .dropdown-menu {
    padding: 10px 0px;
  }
`;

const GifToggleContainer = styled('button')`
  background-color: transparent;
  border: none;
  margin-right: 5px;
`;

const GiphyGridContainer = styled('div')`
  height: 350px;
  overflow-x: hidden;
  overflow-y: auto;
  width: 280px;
`;

const GiphySearchInput = styled('input')`
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  margin-bottom: 10px;
  padding: 8px 12px;
  width: 100%;
  &:focus {
    border-color: ${ColorGreen};
    outline: none;
  }
`;

const GiphySearchWrapper = styled('div')`
  padding: 10px;
  div {
    ::-webkit-scrollbar {
      width: 5px;
      :hover {
        overflow-y: scroll;
      }
    }
    ::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
      border-radius: 10px;
      :hover {
        overflow-y: scroll;
      }
    }
    ::-webkit-scrollbar-thumb {
      -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
      border-radius: 10px;
      :hover {
        overflow-y: scroll;
      }
    }
  }
`;

const PickerWrapper = styled('div')`
  border: 0;
  z-index: 100;
`;

const SmileyContainer = styled('button')`
  background-color: transparent;
  border: none;
  margin-right: 5px;
`;

const AddGifToggle = React.forwardRef(({ children, onClick }, ref) => (
  <GifToggleContainer
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className='dot-box'
  >
    {children}
    <IcomoonReact
      iconSet={iconSet}
      size={30}
      icon='gif'
    />
  </GifToggleContainer>
));

AddGifToggle.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
};

AddGifToggle.defaultProps = {
  children: null,
  onClick: () => {},
};

const AddEmojiToggle = React.forwardRef(({ children, onClick }, ref) => (
  <SmileyContainer
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className='dot-box'
  >
    {children}
    <IcomoonReact
      iconSet={iconSet}
      size={25}
      icon='smiley'
    />
  </SmileyContainer>
));

AddEmojiToggle.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
};

AddEmojiToggle.defaultProps = {
  children: null,
  onClick: () => {},
};

/**
 * @param {Boolean} alignRight aligns the GIF and emoji dropdown menus to the right
 * @param {Function} onSelectEmoji callback invoked when an emoji is selected
 * @param {Function} onSelectGif callback invoked when a GIF is selected
 * @param {Boolean} showGiphyButton determines whether the GIF picker button is rendered
 */
const GiphyEmojiButtons = ({
  alignRight,
  onSelectEmoji,
  onSelectGif,
  showGiphyButton,
}) => {
  const [giphySearchTerm, setGiphySearchTerm] = useState('');
  const [isGiphyOpen, setIsGiphyOpen] = useState(false);

  const emojiButtonRef = useRef(null);
  const giphyButtonRef = useRef(null);

  const handleGifSearch = (offset) => {
    if (giphySearchTerm.trim()) {
      return gf.search(giphySearchTerm, { offset, limit: 10 });
    }
    return gf.trending({ offset, limit: 10 });
  };

  const handleSelectEmoji = (emoji) => {
    onSelectEmoji(emoji);
    emojiButtonRef.current?.click();
  };

  const handleSelectGif = (gifUrl) => {
    onSelectGif(gifUrl);
    giphyButtonRef.current?.click();
  };

  const renderGiphyDropdown = () => (
    <DropDownMenuWrapper className='giphy-dropdown'>
      <Dropdown
        bsPrefix='trick-fix'
        id='dropdown-giphy'
        onToggle={(open) => setIsGiphyOpen(open)}
      >
        <Dropdown.Toggle ref={giphyButtonRef} as={AddGifToggle} aria-label='Add a GIF' />
        <Dropdown.Menu className='dropdown-container giphy' alignRight={alignRight}>
          {isGiphyOpen && (
            <GiphySearchWrapper>
              <GiphySearchInput
                type='text'
                placeholder='Search GIFs...'
                aria-label='Search GIFs'
                value={giphySearchTerm}
                onChange={(e) => setGiphySearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <GiphyGridContainer>
                <Grid
                  key={giphySearchTerm || 'trending'}
                  columns={2}
                  width={260}
                  fetchGifs={handleGifSearch}
                  onGifClick={(gif, e) => {
                    e.preventDefault();
                    handleSelectGif(gif.images.original.url);
                  }}
                  noLink
                />
              </GiphyGridContainer>
            </GiphySearchWrapper>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </DropDownMenuWrapper>
  );

  const renderEmojiDropdown = () => (
    <DropDownMenuWrapper className='emoji-dropdown'>
      <Dropdown bsPrefix='trick-fix'>
        <Dropdown.Toggle ref={emojiButtonRef} as={AddEmojiToggle} aria-label='Add an emoji' />
        <Dropdown.Menu className='dropdown-container emoji' alignRight={alignRight}>
          <PickerWrapper>
            <Picker
              set='apple'
              onSelect={handleSelectEmoji}
              title=''
              emoji=''
              sheetSize={32}
            />
          </PickerWrapper>
        </Dropdown.Menu>
      </Dropdown>
    </DropDownMenuWrapper>
  );

  return (
    <Wrapper>
      <ButtonWrapper>
        {showGiphyButton && renderGiphyDropdown()}
        {renderEmojiDropdown()}
      </ButtonWrapper>
    </Wrapper>
  );
};

GiphyEmojiButtons.propTypes = {
  alignRight: PropTypes.bool,
  onSelectEmoji: PropTypes.func.isRequired,
  onSelectGif: PropTypes.func,
  showGiphyButton: PropTypes.bool,
};

GiphyEmojiButtons.defaultProps = {
  alignRight: false,
  onSelectGif: () => {},
  showGiphyButton: true,
};

export default GiphyEmojiButtons;
